import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useIndustryQuery,
  useSupplierByIdQuery,
  useSupplierDetailsUpdateMutation,
  useSupplierFileCreateMutation,
  useSupplierFileDeleteMutation,
  useSupplierTagsQuery,
  useSupplierTypesListQuery,
} from 'api';
import { FormInstance, FormProps, notification, Upload } from 'components';
import { MAX_FILE_SIZE } from 'constants/index';
import { ErrorMappingValueType } from 'typings';
import { ISupplierDetailsUpdate } from './index';

interface ISupplierForm {
  id?: string;
  name: string;
  verified?: boolean;
  webSite?: string;
  status: string;
  reason?: string;
  type?: string;
  industryId?: string;
  mainCountryId?: string;
  mainLanguageId?: string;
  tags?: string[];
}

export const useSuppliersDetailsEdit = (props: ISupplierDetailsUpdate & { form: FormInstance }) => {
  const { onCancel, form, isOpen } = props;
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(true);
  const { id } = useParams() as { id: string };
  const { data: supplierDetails = {} } = useSupplierByIdQuery({ id });
  const { data: industryData = [] } = useIndustryQuery();
  const { data: supplierTypeData = [] } = useSupplierTypesListQuery();
  const { data: tagData = [] } = useSupplierTagsQuery();

  const [updateSupplierDetails, { isLoading: detailsUpdateIsLoading }] = useSupplierDetailsUpdateMutation();
  const [supplierFileCreate, { isLoading: fileCreateIsLoading }] = useSupplierFileCreateMutation();
  const [supplierFileDelete, { isLoading: fileDeleteIsLoading }] = useSupplierFileDeleteMutation();
  const [notificationApi, contextHolder] = notification.useNotification();

  const initialData = useMemo(
    () => ({
      name: supplierDetails.name,
      verified: supplierDetails.verified,
      webSite: supplierDetails.webSite,
      status: supplierDetails.status,
      reason: supplierDetails.reason,
      type: supplierDetails.type,
      industryId: supplierDetails.industry?.id,
      mainCountryId: supplierDetails.country?.id,
      tags: supplierDetails.manualTags,
      mainLanguageId: supplierDetails.language?.id,
      additionalNotes: supplierDetails.additionalNotes,
      logo: supplierDetails.files?.LOGO
        ? [
            {
              uid: '-1',
              status: 'done',
              name: supplierDetails.files.LOGO.split('fileName=')?.[1] || 'Supplier Logo',
              imgUrl: supplierDetails.files?.LOGO,
            },
          ]
        : [],
    }),
    [supplierDetails]
  );

  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const handleFileSizeValidation = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      form.setFieldValue('logo', []);
      form.setFields([
        {
          name: 'logo',
          errors: [`File exceeds the maximum size of 5 MB.`],
        },
      ]);
      setIsSaveBtnDisabled(true);
      return Upload.LIST_IGNORE;
    } else {
      form.setFields([
        {
          name: 'logo',
          errors: undefined,
        },
      ]);
      setIsSaveBtnDisabled(false);
    }
    return false;
  };

  const onCancelModal = useCallback(() => {
    form.resetFields();
    setIsSaveBtnDisabled(true);
    onCancel();
  }, [form, onCancel]);

  const handleError = useCallback(
    (error: any) => {
      if (!error.data.success) {
        const { data = {} } = error.data;
        Object.keys(data).forEach((_key: string) => {
          openNotificationWithIcon({
            type: 'error',
            message: data[_key],
          });
        });
      } else if (error.data.error) {
        openNotificationWithIcon({
          type: 'error',
          message: 'Something went wrong!',
        });
      }
    },
    [openNotificationWithIcon]
  );

  const onFinish: FormProps<ISupplierForm>['onFinish'] = useCallback(
    async (values: ISupplierForm) => {
      try {
        const { logo } = form.getFieldsValue();
        // TODO need to use this once BE is ready
        // const payload = getUpdatedPayload(values, initialData);

        const payload = { ...values, logo: undefined };
        const shouldDeleteLogo = supplierDetails.files?.LOGO && !logo?.length;
        payload.id = id;
        const formData = new FormData();

        // If logo is selected and not a new logo
        if (logo?.length && id && logo[0]?.uid !== '-1') {
          const file = logo[0];
          formData.append('files', file.originFileObj, file.name);
          formData.append('supplierId', id);
        }
        if (values.status === 'ACTIVE') {
          payload.reason = '';
        }

        const { data, error }: any = await updateSupplierDetails(payload);

        if (data?.success) {
          if (shouldDeleteLogo) {
            const [_, fileName] = supplierDetails.files.LOGO.split('fileName=');
            const deleteResponse = await supplierFileDelete({ name: fileName, supplierId: id });
            if (deleteResponse.data?.success) {
              openNotificationWithIcon({
                type: 'success',
                message: 'The supplier details were successfully updated.',
              });
              onCancelModal();
            } else {
              handleError(deleteResponse.error);
            }
          } else if (formData.has('files')) {
            const createResponse = await supplierFileCreate(formData);
            if (createResponse.data?.success) {
              openNotificationWithIcon({
                type: 'success',
                message: 'The supplier details were successfully updated.',
              });
              onCancelModal();
            } else {
              handleError(createResponse.error);
            }
          } else {
            openNotificationWithIcon({
              type: 'success',
              message: 'The supplier details were successfully updated.',
            });
            onCancelModal();
          }
        } else {
          handleError(error);
        }
      } catch (err) {
        console.log(err, 'error');
      }
    },
    [
      updateSupplierDetails,
      openNotificationWithIcon,
      onCancelModal,
      id,
      supplierFileDelete,
      supplierDetails,
      supplierFileCreate,
      form,
      handleError,
    ]
  );

  useEffect(() => {
    if (isOpen && initialData) {
      form.setFieldsValue(initialData);
    }
  }, [isOpen, form, initialData]);

  return {
    onFinish,
    onCancelModal,
    contextHolder,
    tagData,
    isSaveBtnDisabled,
    handleFileSizeValidation,
    isLoading: detailsUpdateIsLoading || fileCreateIsLoading || fileDeleteIsLoading,
    industryData,
    supplierTypeData,
  };
};
